import { useEffect } from 'react';
import useAssetsStore from '@/app/store/assets';
import useSessionStore from '@/app/store/session';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useColorsStore from '@/app/store/colors';


/**
 * A hook to set assets, session and root organization stores
 * @param {object} getAssetsResponse assets query
 * @param {object} getSessionResponse session query
 * @param {object} getRootOrgaResponse get organization query
 * @param {function} setIsLoading isLoading parent state setter
 * @returns {void}
 */
const useAssetsAndSession = (getAssetsResponse, getSessionResponse, getRootOrgaResponse, setIsLoading) => {
    const { assets, storeAssets } = useAssetsStore();
    const { session, storeSession } = useSessionStore();
    const storeRootOrganization = useRootOrganizationStore(s => s.storeRootOrganization);
    const { setColor, setTextColor, setIsLight, setDynamicEnlightedColor } = useColorsStore();

    // Fetch assets
    useEffect(() => {
        if (getAssetsResponse && !getAssetsResponse.isLoading && getAssetsResponse.data && !assets) {
            storeAssets(getAssetsResponse.data);
        }
    }, [assets, getAssetsResponse.data, getAssetsResponse.isLoading]);


    // Fetch session
    useEffect(() => {
        if (getSessionResponse && !getSessionResponse.isLoading) {
            if (!getSessionResponse.isError && getSessionResponse.data) {
                storeSession(getSessionResponse.data);
            } else {
                storeSession(null);
            }
        }
    }, [getSessionResponse.data, getSessionResponse.isLoading, getSessionResponse.isError]);


    // Fetch root organization
    useEffect(() => {
        if (session && !getRootOrgaResponse.isLoading && !getRootOrgaResponse.isError && getRootOrgaResponse.data) {
            storeRootOrganization(getRootOrgaResponse.data);
            setColor(getRootOrgaResponse.data.brand.style.mainColor);
            setTextColor(getRootOrgaResponse.data.brand.style.mainColor);
            setIsLight(getRootOrgaResponse.data.brand.style.mainColor);
            setDynamicEnlightedColor(getRootOrgaResponse.data.brand.style.mainColor);
        }
        if (session && !getRootOrgaResponse.data && !getRootOrgaResponse.isLoading && !getRootOrgaResponse.isError) {
            getRootOrgaResponse.refetch(true, session.organization);
        }
    }, [session, getRootOrgaResponse.isLoading, getRootOrgaResponse.data, getRootOrgaResponse.isError]);

    // Manage loading state based on all fetches
    useEffect(() => {
        const allLoading = getAssetsResponse.isLoading || getSessionResponse.isLoading || getRootOrgaResponse.isLoading;

        const allFetched = getAssetsResponse.isFetched && getSessionResponse.isFetched && getRootOrgaResponse.isFetched;

        const allFinished = (allFetched && !allLoading && !(getSessionResponse.isError && getSessionResponse.data === null))
            // If not connected and get Session is fetched with error
            || (getSessionResponse.isError && getSessionResponse.isFetched);

        setIsLoading(!allFinished);
    }, [
        getAssetsResponse.isLoading,
        getAssetsResponse.isFetched,
        getSessionResponse.isLoading,
        getSessionResponse.isFetched,
        getSessionResponse.isError,
        getRootOrgaResponse.isLoading,
        getRootOrgaResponse.isFetched
    ]);
};

export default useAssetsAndSession;
